import type { GalleryItemData } from './components/GalleryItem/GalleryItem';
import type { ValuePropLike } from './propValues.types';
import { assetForBreakpoint, getVisibleSlides, isValuePropLike } from './propValues.helpers';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { useCallback, useMemo } from 'react';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

// Card previews are lazy-rendered, page size is no longer a performance concern.
const PAGE_SIZE = 100;
const PAGE_QUERY_PARAM = 'page';

// Value-prop tiles come in two shapes: a single prop (`prop`) and the auto-rotating list
// (`propCarousel`). Both are laid out by `gridArea` and shown on page 1, so pagination must treat
// them identically — otherwise the carousel is bucketed with templates and sliced off the page.
// A carousel has no top-level contentList; it is renderable when at least one slide has an asset
// for the current breakpoint, using the same `getVisibleSlides` logic ValuePropListCard renders by.
const isValidValueProp = (prop: ValuePropLike, isMobile: boolean) =>
  prop.type === 'propCarousel' ? getVisibleSlides(prop.slides, isMobile).length > 0 : !!assetForBreakpoint(prop.contentList, isMobile);

export const useStationeryPagination = (templatesWithProps: ReadonlyArray<GalleryItemData>, columnCount: number, isSearchActive: boolean, pageSize: number = PAGE_SIZE) => {
  const isMobile = useIsMobileScreen();
  const [pageQueryParam, setPage] = useManagedSearchParam(PAGE_QUERY_PARAM);
  const requestedPage = Number(pageQueryParam || '1');
  const currentPage = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;

  const { items, pages, effectivePage }: { items: GalleryItemData[]; pages: number; effectivePage: number } = useMemo(() => {
    // When search is active, value props are injected upstream by search in a specific order, and we just paginate the flat list.
    if (isSearchActive) {
      const pages = Math.ceil(templatesWithProps.length / pageSize);
      const effectivePage = Math.min(currentPage, pages || 1);
      const offset = (effectivePage - 1) * pageSize;
      return {
        items: templatesWithProps.slice(offset, offset + pageSize) as GalleryItemData[],
        pages,
        effectivePage,
      };
    }

    const valueProps = templatesWithProps.filter(isValuePropLike);
    const validValueProps = valueProps.filter(prop => isValidValueProp(prop, isMobile));

    const templatesWithoutValueProps = templatesWithProps.filter(template => !isValuePropLike(template));
    const pages = Math.ceil(templatesWithoutValueProps.length / pageSize);
    const effectivePage = Math.min(currentPage, pages || 1);

    const templatesOffset = pageSize * (effectivePage - 1);

    const templatesToDisplay = templatesWithoutValueProps.slice(templatesOffset, templatesOffset + pageSize);
    // Keep promos within the rows filled by this page's regular tiles. They remain extras,
    // but cannot create distant rows below a short page or sustain a tail of repeated promos.
    const regularTileRows = Math.ceil(templatesToDisplay.length / columnCount);
    const valuePropsToDisplay = effectivePage > 1 ? [] : validValueProps.filter(prop => prop.row < regularTileRows);
    const items = [...templatesToDisplay, ...valuePropsToDisplay];

    return { items, pages, effectivePage };
  }, [templatesWithProps, currentPage, isMobile, columnCount, isSearchActive, pageSize]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page.toString());
    },
    [setPage],
  );

  return { items, page: effectivePage, pages, setPage: handlePageChange };
};
